import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as templateService from "../service/templateService";
import type {
  AcademicStructureTemplateListRow,
  CreateTemplatePayload,
  UpdateTemplatePayload,
  TemplateStructure,
  AddNodePayload,
  Pagination,
} from "../types";

export const loadTemplates = createAsyncThunk(
  "academicStructureTemplates/loadTemplates",
  async (params: { status?: string; institutionType?: string; search?: string; page?: number; limit?: number } = {}) => {
    return await templateService.fetchTemplates(params);
  }
);

export const addTemplate = createAsyncThunk(
  "academicStructureTemplates/addTemplate",
  async (payload: CreateTemplatePayload, { dispatch }) => {
    const template = await templateService.createTemplate(payload);
    await dispatch(loadTemplates({}));
    return template;
  }
);

export const editTemplate = createAsyncThunk(
  "academicStructureTemplates/editTemplate",
  async ({ id, payload }: { id: number; payload: UpdateTemplatePayload }, { dispatch }) => {
    const template = await templateService.updateTemplate(id, payload);
    await dispatch(loadTemplates({}));
    return template;
  }
);

export const removeTemplate = createAsyncThunk(
  "academicStructureTemplates/removeTemplate",
  async (id: number, { dispatch }) => {
    await templateService.deleteTemplate(id);
    await dispatch(loadTemplates({}));
  }
);

export const loadTemplateStructure = createAsyncThunk(
  "academicStructureTemplates/loadStructure",
  async (id: number) => {
    return await templateService.fetchTemplateStructure(id);
  }
);

export const addNode = createAsyncThunk(
  "academicStructureTemplates/addNode",
  async ({ templateId, payload }: { templateId: number; payload: AddNodePayload }, { dispatch }) => {
    await templateService.addTemplateNode(templateId, payload);
    await dispatch(loadTemplateStructure(templateId));
    await dispatch(loadTemplates({}));
  }
);

export const updateNode = createAsyncThunk(
  "academicStructureTemplates/updateNode",
  async (
    { templateId, nodeId, payload }: { templateId: number; nodeId: number; payload: Partial<AddNodePayload> },
    { dispatch }
  ) => {
    await templateService.updateTemplateNode(templateId, nodeId, payload);
    await dispatch(loadTemplateStructure(templateId));
  }
);

export const removeNode = createAsyncThunk(
  "academicStructureTemplates/removeNode",
  async ({ templateId, nodeId }: { templateId: number; nodeId: number }, { dispatch }) => {
    await templateService.removeTemplateNode(templateId, nodeId);
    await dispatch(loadTemplateStructure(templateId));
    await dispatch(loadTemplates({}));
  }
);

interface TemplateState {
  templates: AcademicStructureTemplateListRow[];
  pagination: Pagination;
  loading: boolean;
  error: string | null;

  structure: TemplateStructure | null;
  structureLoading: boolean;

  mutating: boolean;
  mutateError: string | null;
}

const initialState: TemplateState = {
  templates: [],
  pagination: { page: 1, limit: 10, total: 0, totalPages: 1 },
  loading: false,
  error: null,

  structure: null,
  structureLoading: false,

  mutating: false,
  mutateError: null,
};

const templateSlice = createSlice({
  name: "academicStructureTemplates",
  initialState,
  reducers: {
    clearMutateError: (state) => {
      state.mutateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload.templates;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load templates";
      })
      .addCase(addTemplate.pending, (state) => {
        state.mutating = true;
        state.mutateError = null;
      })
      .addCase(addTemplate.fulfilled, (state) => {
        state.mutating = false;
      })
      .addCase(addTemplate.rejected, (state, action) => {
        state.mutating = false;
        state.mutateError = action.error.message ?? "Failed to add template";
      })
      .addCase(editTemplate.rejected, (state, action) => {
        state.mutateError = action.error.message ?? "Failed to update template";
      })
      .addCase(removeTemplate.rejected, (state, action) => {
        state.mutateError = action.error.message ?? "Failed to delete template";
      })
      .addCase(loadTemplateStructure.pending, (state) => {
        state.structureLoading = true;
      })
      .addCase(loadTemplateStructure.fulfilled, (state, action) => {
        state.structureLoading = false;
        state.structure = action.payload;
      })
      .addCase(loadTemplateStructure.rejected, (state, action) => {
        state.structureLoading = false;
        state.error = action.error.message ?? "Failed to load template structure";
      })
      .addCase(addNode.rejected, (state, action) => {
        state.mutateError = action.error.message ?? "Failed to add node";
      })
      .addCase(updateNode.rejected, (state, action) => {
        state.mutateError = action.error.message ?? "Failed to update node";
      })
      .addCase(removeNode.rejected, (state, action) => {
        state.mutateError = action.error.message ?? "Failed to remove node";
      });
  },
});

export const { clearMutateError } = templateSlice.actions;
export default templateSlice.reducer;
